import { Button } from "@admin/components/atoms/Button";
import { Input, Option, Select } from "@admin/components/atoms/Input";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import { Shift, updateOpenShift } from "@enos-monorepo/lib";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface EditOpenShiftModalProps extends ModalProps {
  shift?: Shift;
}
export function EditOpenShiftModal({
  shift,
  ...props
}: EditOpenShiftModalProps) {
  const { t } = useTranslation();
  const [calculatedTime, setCalculatedTime] = useState("");
  const [beginningTime, setBeginningTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const { mutate, isPending } = useMutation({ mutationFn: updateOpenShift });
  const queryClient = useQueryClient();

  const handleEditSubmit = () => {
    mutate(
      {
        id: shift?.id as string,
        plannedStartTime: `${shift?.plannedStartDate?.split("T")[0]}T${beginningTime}`,
        plannedEndTime: `${shift?.plannedEndDate?.split("T")[0]}T${endTime}`,
      },
      {
        onSuccess: () => {
          props?.onClose?.();
          queryClient.invalidateQueries({
            queryKey: ["shifts"],
          });
          toast.success(t("shift.The shift has been edited"));
        },

        onError: (error) => {
          toast.error(t("shift.Unable to edit the shift"), {
            duration: 5000,
          });
        },
      },
    );
  };

  useEffect(() => {
    if (shift && shift.plannedStartDate && shift.plannedEndDate) {
      setBeginningTime(extractTime(shift.plannedStartDate));
      setEndTime(extractTime(shift.plannedEndDate));
    }
  }, [shift]);

  useEffect(() => {
    if (beginningTime && endTime) {
      const diffTime = calculateTimeDifference(beginningTime, endTime);
      setCalculatedTime(diffTime);
    } else {
      setCalculatedTime("");
    }
  }, [beginningTime, endTime]);

  return (
    <Modal
      {...props}
      title={t("shift.Edit Open Shift for Today")}
      actionButtons={
        <>
          <Button
            variant="secondary"
            onClick={props?.onClose}
            disabled={isPending}
          >
            {t("common.Discard")}
          </Button>
          <Button
            variant="primary"
            onClick={handleEditSubmit}
            loading={isPending}
          >
            {t("shift.Edit Open Shift")}
          </Button>
        </>
      }
    >
      <div className="flex items-baseline flex-row lg:flex-nowrap gap-2.5">
        <label className="form-label flex items-center gap-1 max-w-56 !font-normal !text-gray-800">
          {t("shift.Team Restrictions")}
        </label>
        <Select
          options={[{ value: 1, label: "Coming Soon" }] as Option[]}
          placeholder="Soon..."
          size="md"
          disabled
        />
      </div>
      <div className="flex items-baseline flex-row lg:flex-nowrap gap-2.5">
        <label className="form-label flex items-center gap-1 max-w-56 !font-normal !text-gray-800">
          {t("shift.Shift Beginning and End")}
        </label>
        <div className="flex flex-row justify-between w-full gap-2">
          <div className="flex flex-col w-full gap-y-1">
            <Input
              type="time"
              value={beginningTime}
              onChange={(e) => setBeginningTime(e.target.value)}
            />
          </div>
          <div className="flex flex-col w-full gap-y-1">
            <Input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </div>
        </div>
      </div>
      <div className="flex items-baseline flex-row lg:flex-nowrap gap-2.5">
        <label className="form-label flex items-center gap-1 max-w-56 !font-normal !text-gray-800">
          {t("shift.Calculated Time")}
        </label>
        <Input value={calculatedTime} disabled />
      </div>
    </Modal>
  );
}

function extractTime(dateTimeString: string) {
  return dateTimeString.split("T")[1].slice(0, 5);
}

function calculateTimeDifference(start: string, end: string) {
  const [startHour, startMinute] = start.split(":").map(Number);
  const [endHour, endMinute] = end.split(":").map(Number);

  const startDate = new Date();
  const endDate = new Date();

  startDate.setHours(startHour, startMinute, 0, 0);
  endDate.setHours(endHour, endMinute, 0, 0);

  if (endDate < startDate) {
    endDate.setDate(endDate.getDate() + 1);
  }

  const diffMs = endDate.getTime() - startDate.getTime();
  const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  return `${diffHrs.toString().padStart(2, "0")}:${diffMins
    .toString()
    .padStart(2, "0")}`;
}
